import { amorphicStatic, supertypeClass } from '@haventech/amorphic';
import { RedoException } from '@hit/orca';
import { OldInteraction } from '../webservice/Interaction';
import { Policy } from '../customer/Policy';
import { PolicyDoc } from '../customer/PolicyDoc';
import { WorkflowEmails } from './WorkflowEmails';
import { Utils } from '../Utils';

import { HitActivityInterface } from '../../../hit-workflow/js/HitActivityInterface';
import { TimeUnit } from '@hit/utils';
import { HitActivity } from '../../../hit-workflow/js/HitActivity';
import { HitAppCtx } from '../orca/HitAppCtx';
import { PolicyDocQuery } from '../customer/PolicyDocQuery';
import { Assumptions, DocTypes } from '../static/Assumptions';
import { WsRequest } from '../wsclient/WsRequest';
import { Application } from '../customer/Application';
import { POLICY_CHANGE_HISTORY_STATUS } from '../../js/static/PolicyChangeHistoryStatus';
import { PolicyAmendmentProperty } from '../../js/static/PolicyAmendmentProperty';
import { CollectionItemChange } from '../../js/customer/PolicyChangeHistory';
import { POLICY_DOC_SCAN_STATUS, POLICY_DOC_SOURCES } from '../constants/policy-doc';
import { UICategory, UILoggerSingleton } from '../logger/UILoggerSingleton';
import { LaunchDarklyInstance, LD_APP } from '../../../common/js/launch-darkly/LaunchDarklySingleton';
import { FeatureFlags } from '../../../coverpath-common/js/interfaces/CoverpathTypes';

export interface PolicyDocForm {
  name: string;
  roles: Array<string>;
  order: number;
  body;
  type: string;
}

export interface ConversationMetadata {
  workflowId: string;
  stageId: string;
  activityId: string;
  applicationId: string;
}

@supertypeClass({ toClient: false, toServer: false })
export class WorkflowUtils {
  static getConfigPropByChannel(channel: string, configProp: string) {
    const channelName = Utils.getAppName(channel);
    const config = amorphicStatic.config.configStore[channelName];

    return config.get(configProp);
  }

  static adaptTrigger(trigger) {
    let interaction;
    let message;

    if (trigger instanceof OldInteraction) {
      interaction = trigger;
    } else {
      message = trigger;
    }

    return {
      get interaction() {
        return interaction;
      },
      get message() {
        return message;
      },
      get metadata() {
        return trigger ? trigger.metadata : null;
      },
      toDebugString: function () {
        return interaction
          ? 'trigger interaction: ' + interaction.toDebugString()
          : 'trigger message: ' + JSON.stringify(message);
      },
    };
  }

  static acctCenterUrl(channel: string, language?: string): string {
    let prop = 'accountCenterUrl';

    if (language === 'es') {
      prop = prop + 'Es';
    }

    return WorkflowUtils.getConfigPropByChannel(channel, prop);
  }

  static acctCenterDetailedViewUrl(channel: string, applicationNumber: string, language?: string): string {
    return `${WorkflowUtils.getConfigPropByChannel(
      channel,
      'accountCenterDetailedViewUrl'
    )}?applicationNumber=${applicationNumber}`;
  }

  static acctCenterUrlSI(channel: string): string {
    const prop = 'simplifiedIssueOrigin';

    return WorkflowUtils.getConfigPropByChannel(channel, prop) + '/account-center';
  }

  static homePageUrl(channel: string, language?: string): string {
    let prop = 'homePageUrl';

    if (language === 'es') {
      prop = `${prop}Es`;
    }

    return WorkflowUtils.getConfigPropByChannel(channel, prop);
  }

  static havenSIConversionUrl(channel: string, firstName, face, term, quote): string {
    const prop = 'simplifiedIssueOrigin';
    const utmParams = '&utm_source=si_email&utm_medium=email&utm_campaign=wb';
    return (
      WorkflowUtils.getConfigPropByChannel(channel, prop) +
      '/welcome-back?firstName=' +
      firstName +
      '&face=' +
      face +
      '&term=' +
      term +
      '&quote=' +
      quote +
      utmParams
    );
  }

  static echo(value) {
    return value;
  }

  static done(): string {
    return HitActivity.RESULTS.DONE;
  }

  static skipped(): string {
    return HitActivity.RESULTS.DONE_SKIPPED;
  }

  static notdone(): string {
    return HitActivity.RESULTS.NOT_DONE;
  }

  static redoAfter(offset: number, unit: TimeUnit, result: string): never {
    throw new RedoException(result, Utils.offsetCurrentTime(offset, unit));
  }

  static redoAfterDate(offset: number, unit: string, result: string): () => void {
    return function () {
      throw new RedoException(result, Utils.offsetDate(Utils.now(), offset, unit));
    };
  }

  static async interactWithWebService(
    serviceClassName,
    urlPath: string,
    request: WsRequest,
    activity,
    handleServiceResponse?: (r, i) => void
  ): Promise<any> {
    const workflow = activity.hitWorkflow;
    const convRefId = request.data.getConvRefId();
    const metadata = {
      workflowId: workflow._id,
      stageId: workflow.currentStage._id,
      activityId: activity._id,
      policyId: workflow.policy._id,
    };

    const interaction = await activity.lastExecution.interact(
      convRefId,
      metadata,
      serviceClassName,
      request.data.doc(),
      invokeService
    );

    return interaction;

    function invokeService(interaction) {
      const svcCfg = {
        hierarchy: workflow.hierarchy,
        dimension: workflow.dimensions,
      };

      const wsclient = request.client;
      wsclient.appendConfig(svcCfg);
      return wsclient.invoke(request.data.format(), urlPath, workflow.policy.getRunModes()).then(formatResponse);

      function formatResponse(response) {
        return handleServiceResponse ? handleServiceResponse(response, interaction) : Promise.resolve(response);
      }
    }
  }

  static async addPolicyDoc(
    name: string,
    doc: string,
    type: string,
    src: string,
    policy: Policy,
    appType?: string,
    docType?: string,
    uploadedBy?: string,
    language?: string,
    status?: POLICY_DOC_SCAN_STATUS
  ) {
    const lang = language || 'en';
    const txn = amorphicStatic.beginTransaction();

    const policyDoc: PolicyDoc = PolicyDoc.new(
      name,
      doc,
      type,
      src,
      policy,
      appType,
      docType,
      uploadedBy,
      lang,
      null,
      null,
      status
    );

    await policyDoc.persistorSave({
      transaction: txn,
    });

    return amorphicStatic.commit({ transaction: txn });
  }

  static addPolicyDocThroughActivity(
    name: string,
    doc: string,
    type: string,
    src: string,
    policy: Policy,
    appType?: string,
    docType?: string,
    language?: string,
    status?: POLICY_DOC_SCAN_STATUS
  ) {
    const lang = language || 'en';

    const policyDoc = PolicyDoc.new(name, doc, type, src, policy, appType, docType, null, lang, null, null, status);

    HitAppCtx.get.exnCtx.dbTxn.setDirty(policyDoc);
  }

  static setITMIndicator(policy: Policy) {
    policy.application.isITM = true;
    HitAppCtx.get.exnCtx.dbTxn.setDirty(policy.application);
  }

  static scheduleFrom(fromDate, offsetDuration: number, timeUnit: string, overrideTime): any {
    let dt = Utils.offsetTime(fromDate, offsetDuration, timeUnit as TimeUnit);

    if (overrideTime) {
      dt = new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate(), overrideTime.hr, overrideTime.min));
    }

    return dt;
  }

  static schedule(offsetDuration: number, timeUnit: string, overrideTime): any {
    return WorkflowUtils.scheduleFrom(Utils.now(), offsetDuration, timeUnit as TimeUnit, overrideTime);
  }

  //TODO HL-11513 Type return
  static ownerSchedule(offsetDuration: number, timeUnit: TimeUnit, overrideTime, policy: Policy): any {
    return policy.toGmtFromOwnerTimezone(WorkflowUtils.schedule(offsetDuration, timeUnit, overrideTime));
  }

  static async emailAppCanceledDueToNoAction(activity) {
    const channel = activity.workflow.policy.getChannelCode();
    const language = activity.workflow.policy.getCustomerLanguageCode();
    const acctCenterUrl = WorkflowUtils.acctCenterUrl(channel, language);

    return WorkflowUtils.emailInsuredAndOwner(
      activity,
      await WorkflowEmails.sendNotifyAppCancelledSign.bind(null, activity, acctCenterUrl)
    );
  }

  static emailInsuredAndOwner(activity: HitActivityInterface, email) {
    const policy = activity.hitWorkflow.policy;
    const persons = [policy.owner];

    if (!policy.isOwnerSameAsInsured()) {
      persons.push(policy.insured.person);
    }

    return Promise.all(persons.map(email));
  }

  static getPolicyDocs(policy: Policy, session) {
    return PolicyDocQuery.persistorFetchByQuery(
      {
        policyId: policy._id,
      },
      { session: session }
    );
  }
  static addPolicyDocsWithOptionsThroughActivity(forms: Array<PolicyDocForm>, policy: Policy, options) {
    const type = options.type;
    const appType = options.appType ? options.appType : '';

    forms.forEach((form) => {
      const policyDoc = PolicyDoc.new(
        form.name,
        form.body,
        type,
        POLICY_DOC_SOURCES.app,
        policy,
        appType,
        form.type,
        'Workflow',
        '',
        form.roles,
        form.order
      );
      HitAppCtx.get.exnCtx.dbTxn.setDirty(policyDoc);
    });
  }

  static retrievePolicyDocs(
    policy: Policy,
    appType: string,
    session,
    virusScanStatus?: Array<POLICY_DOC_SCAN_STATUS>
  ): Promise<Array<{ id: string; name: string; date: Date; docS3: string }>> {
    const innerStatusMap = [];
    virusScanStatus.forEach((virusScanStatus) => {
      innerStatusMap.push({ status: virusScanStatus });
    });
    const queryParams = {
      $and: [
        {
          policyId: policy._id,
        },
        {
          appType: appType,
        },
        {
          $or: innerStatusMap,
        },
      ],
    };
    return PolicyDocQuery.persistorFetchByQuery(queryParams, { session: session }).then((policyDocs) => {
      UILoggerSingleton.getInstance().info({
        module: 'WorkflowUtils',
        function: 'retrievePolicyDocs-length',
        category: UICategory.request,
        data: policyDocs.length,
      });

      if (policyDocs.length < 1) {
        return false;
      }
      return policyDocs.map((doc) => {
        return { id: doc._id, name: doc.name, date: doc.date, docS3: doc.docS3 };
      });
    });
  }

  static archivePolicyDocs(policy: Policy, appType: string, session): Promise<boolean> {
    return PolicyDoc.persistorFetchByQuery(
      {
        policyId: policy._id,
        appType: appType,
        isActive: 'true',
      },
      { session: session }
    ).then((policyDocs) => {
      if (policyDocs.length < 1) {
        return null;
      }
      const txn = amorphicStatic.beginTransaction();
      policyDocs.forEach((policyDoc) => {
        policyDoc.appType = 'archive';
        policyDoc.persistorSave({ transaction: txn });
      });
      return amorphicStatic.commit({ transaction: txn });
    });
  }

  static deletePolicyDocs(policy: Policy, session, id?: string) {
    return PolicyDoc.persistorFetchByQuery(
      {
        policyId: policy._id,
        _id: id ? id : '',
        isActive: 'true',
      },
      { session: session }
    ).then((policyDocs) => {
      if (policyDocs.length >= 1) {
        const txn = amorphicStatic.beginTransaction();

        policyDocs.forEach((policyDoc) => {
          policyDoc.persistDelete({ transaction: txn });
        });

        return amorphicStatic.commit({ transaction: txn });
      } else {
        return false;
      }
    });
  }

  static archivePreviousDocMetadata(policy: Policy, docType: DocTypes) {
    const filteredDocMetadata = policy.eSignDocMetadata.filter((doc) => doc.docType === docType);
    filteredDocMetadata.map((docMetadata) => {
      docMetadata.docType = Assumptions.policyDocTypes.Archive;
      HitAppCtx.get.exnCtx.dbTxn.setDirty(docMetadata);
      return docMetadata;
    });
    policy.setPolicySignedDateByInsured(null);

    if (policy.isOwnerSameAsInsured()) {
      policy.setPolicySignedDateByOwner(null);
    }
    HitAppCtx.get.exnCtx.dbTxn.setDirty(policy);
  }

  static async getLinkForCoverpathAgentEsignEmail(linkId: string, application): Promise<string> {
    const baseUrl = amorphicStatic.config.nconf.get('coverpathAgentBaseUrl');
    const encodedLinkId = Utils.base64(linkId, true);

    const ldInstance = await LaunchDarklyInstance.getInstance(LD_APP.cp);
    const caseRedesignEnabled = await ldInstance.getFeatureFlagValueAsBooleanWithIDAndCustom(
      FeatureFlags.caseRedesignEnabled,
      application.primaryPolicy.primaryPolicyAgent.primaryAgent.producerId,
      { email: application.primaryPolicy.primaryPolicyAgent.primaryAgent.party.email }
    );
    return `${baseUrl}${caseRedesignEnabled ? 'case-details' : 'client-details'}?id=${encodedLinkId}`;
  }

  static updateApplicationWithAmendmentValues(application: Application) {
    WorkflowUtils.updatePolicyWithAmendmentValues(application.primaryPolicy);
  }

  static updatePolicyWithAmendmentValues(policy: Policy) {
    const pendingAmendments = policy.getPendingAmendments();

    UILoggerSingleton.getInstance().info(
      {
        module: 'WorkflowhUtils',
        function: 'updatePolicyWithAmendmentValues',
        category: UICategory.request,
        data: {
          policyNumber: policy.policyNumber,
          policyId: policy._id,
          numPendingPCH: pendingAmendments.length,
        },
      },
      'Determining if policy has pending amendments to apply'
    );

    if (pendingAmendments.length > 0) {
      UILoggerSingleton.getInstance().info(
        {
          module: 'WorkflowhUtils',
          function: 'updatePolicyWithAmendmentValues',
          category: UICategory.request,
          data: {
            policyNumber: policy.policyNumber,
            policyId: policy._id,
          },
        },
        'Applying changes to policy'
      );

      if (policy.isPrimary) {
        policy.term = policy.termWithPendingAmendment();
        policy.paymentFrequency = policy.paymentFrequencyWithPendingAmendment();
        policy.waiver = policy.hasWaiverWithPendingAmendment();
        policy.faceAmount = policy.faceAmountWithPendingAmendment();
        policy.premiumBreakdown.waiver = policy.premiumBreakdown.waiverAmountWithPendingAmendment();
        policy.premiumBreakdown.base = policy.premiumBreakdown.baseWithPendingPremiumAmendment();
        policy.product = policy.product;
        policy.productType = policy.productType;

        const flatExtraPCH = policy.policyChanges.find(
          (item) =>
            item.property === PolicyAmendmentProperty.FLAT_EXTRA_JSON &&
            item.status === POLICY_CHANGE_HISTORY_STATUS.PENDING
        );

        if (!!flatExtraPCH) {
          const flatExtraAmendments: CollectionItemChange[] = JSON.parse(flatExtraPCH.newValue);
          flatExtraAmendments.forEach((change) => {
            const modelValue = policy.premiumBreakdown.flatExtras.find(
              (val) => val[change.uniquePropertyName] === change.uniquePropertyValue
            );

            if (modelValue) {
              modelValue[change.propBeingChanged] = change.newValue;
            } else {
              throw new Error(`Problem applying flatExtra amendment for ${change.uniquePropertyName}`);
            }
          });
        }
      }

      UILoggerSingleton.getInstance().info(
        {
          module: 'WorkflowUtils',
          function: 'updatePolicyWithAmendmentValues',
          category: UICategory.request,
          data: {
            policyNumber: policy.policyNumber,
            policyId: policy._id,
          },
        },
        'Updating status on policy change history records'
      );

      pendingAmendments.forEach((pch) => {
        if (pch.status === POLICY_CHANGE_HISTORY_STATUS.PENDING) {
          pch.status = POLICY_CHANGE_HISTORY_STATUS.COMPLETE;
        }
      });
    }
  }

  static checkIfPaymentAndBeneficiaryInfoExist(policy: Policy, moduleName: string, activityName: string) {
    const allPaymentInfoExists = !!(policy.paymentABA && policy.paymentAccount && policy.paymentAccountType);
    const beneInfoExists = !!policy.beneficiaryType;
    if (!allPaymentInfoExists || !beneInfoExists) {
      const error = {
        isHumanRelated: false,
        data: {
          policyNumberExists: !!policy.policyNumber,
          paymentABAExists: !!policy.paymentABA,
          paymentAccountExists: !!policy.paymentAccount,
          paymentAccountTypeExists: !!policy.paymentAccountType,
          beneficiaryTypeExists: !!policy.beneficiaryType,
          paymentCCExists: !!policy.paymentCCCardType,
        },
      };
      UILoggerSingleton.getInstance().error(
        {
          module: moduleName,
          function: activityName,
          category: UICategory.request,
          error: error,
        },
        'Missing payment and/or Beneficiary info'
      );

      throw new Error('Missing payment and/or Beneficiary info');
    }
  }

  static async fetchWorkFlowDetailsByPolicyNumber(
    policyNumber: string,
    workflowName: string,
    template: string
  ): Promise<ConversationMetadata> {
    const query = `SELECT "w"."_id" "workflowId", "w"."name","w"."currentStage_id" "stageId", "pol"."application_id" "applicationId", "act"."_id" "activityId"  FROM "policy" AS "pol"
                   JOIN "workflow" as "w" ON "w"."referenceId" = "pol"."application_id"
                   JOIN "stage" as "s" ON "w"."currentStage_id" = "s"."_id"
                   JOIN "activity" as "act" ON "act"."stage_id" = "s"."_id"
                   WHERE "pol"."policyNumber" = ?
                   AND "w"."name" = ?
                   AND "w"."referenceType" = ?
                   AND "act"."_template" = ? `;
    const result = await Policy.getKnex().client.raw(query, [policyNumber, workflowName, 'ApplicationId', template]);
    return result && result.rows[0];
  }
}
